import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu, Newspaper, FileText, LogOut, Home, BarChart3, SlidersHorizontal, Cable, Layers, X, Building2, LayoutTemplate } from 'lucide-react';
import { Avatar } from './ui/avatar';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { motion, AnimatePresence } from 'framer-motion';
import { clients, articles, rassegne, sources, reports, tierLabels } from '../lib/data';
const navSections = [{
  title: null,
  items: [{
    icon: Home,
    label: 'Panoramica',
    path: '/home'
  }, {
    icon: Newspaper,
    label: 'Rassegne',
    path: '/rassegne'
  }, {
    icon: BarChart3,
    label: 'Statistiche',
    path: '/statistics'
  }, {
    icon: FileText,
    label: 'Report',
    path: '/reports'
  }]
}, {
  title: 'CONFIGURAZIONE',
  items: [{
    icon: SlidersHorizontal,
    label: 'Algoritmo',
    path: '/settings/algorithm'
  }, {
    icon: Cable,
    label: 'Connettori',
    path: '/settings/connectors'
  }, {
    icon: Layers,
    label: 'Gestione Tier',
    path: '/settings/tiers'
  }]
}];
function MobileSearchOverlay({
  onClose
}: {
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);
  const lowerQuery = query.toLowerCase();
  const isSearching = query.trim().length > 0;
  const filteredArticles = isSearching ? articles.filter(a => a.title.toLowerCase().includes(lowerQuery) || a.source.toLowerCase().includes(lowerQuery) || a.author.toLowerCase().includes(lowerQuery)) : articles.slice(0, 3);
  const filteredRassegne = isSearching ? rassegne.filter(r => r.name.toLowerCase().includes(lowerQuery) || r.provider.toLowerCase().includes(lowerQuery)) : rassegne.slice(0, 3);
  const filteredSources = isSearching ? sources.filter(s => s.name.toLowerCase().includes(lowerQuery)) : sources.slice(0, 3);
  const filteredReports = isSearching ? reports.filter(r => r.name.toLowerCase().includes(lowerQuery)) : reports.slice(0, 3);
  const hasResults = filteredArticles.length > 0 || filteredRassegne.length > 0 || filteredSources.length > 0 || filteredReports.length > 0;
  return <motion.div key="mobile-search-overlay" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: 'easeInOut'
  }} className="fixed inset-0 bg-white flex flex-col" style={{
    zIndex: 99999
  }} data-id="element-46">
      {/* Top bar */}
      <div className="h-14 border-b border-border flex items-center justify-between px-4 flex-shrink-0 bg-white" data-id="element-47">
        <button type="button" onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-muted transition-colors" aria-label="Chiudi Ricerca" data-id="element-48">
          <X className="h-5 w-5 text-foreground" data-id="element-49" />
        </button>
        <span className="font-medium text-sm text-foreground" data-id="element-50">Ricerca</span>
        <div className="w-9" data-id="element-51" />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pt-6 pb-24" data-id="element-52">
        {/* Search Input */}
        <div className="relative mb-8" data-id="element-53">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" data-id="element-54" />
          <input type="text" autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca articoli, rassegne, testate..." className="w-full pl-12 pr-4 py-4 text-base border border-border rounded-2xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent" data-id="element-55" />
        </div>

        {!hasResults ? <div className="text-center py-12" data-id="element-56">
            <Search className="h-7 w-7 text-muted-foreground mx-auto mb-3 opacity-50" data-id="element-57" />
            <p className="text-sm text-muted-foreground" data-id="element-58">
              Nessun risultato per "{query}"
            </p>
          </div> : <div className="space-y-8" data-id="element-59">
            {!isSearching && <div className="text-sm font-medium text-muted-foreground" data-id="element-60">
                Suggerimenti
              </div>}

            {/* Articoli */}
            {filteredArticles.length > 0 && <section data-id="element-61">
                <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-62">
                  <FileText className="h-3.5 w-3.5" data-id="element-63" />
                  Articoli
                </div>
                <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border" data-id="element-64">
                  {filteredArticles.map(article => <button key={article.id} onClick={() => {
              onClose();
              navigate(`/article/${article.id}`);
            }} className="w-full text-left px-4 py-3.5 hover:bg-muted/30 transition-colors" data-id="element-65">
                      <div className="text-sm font-medium text-foreground line-clamp-2 mb-1" data-id="element-66">
                        {article.title}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2" data-id="element-67">
                        <span data-id="element-68">{article.source}</span>
                        <span data-id="element-69">·</span>
                        <span data-id="element-70">{article.relevanceScore.toFixed(1)}</span>
                      </div>
                    </button>)}
                </div>
              </section>}

            {/* Rassegne */}
            {filteredRassegne.length > 0 && <section data-id="element-71">
                <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-72">
                  <Newspaper className="h-3.5 w-3.5" data-id="element-73" />
                  Rassegne
                </div>
                <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border" data-id="element-74">
                  {filteredRassegne.map(r => <button key={r.id} onClick={() => {
              onClose();
              navigate(`/rassegne/${r.id}`);
            }} className="w-full text-left px-4 py-3.5 hover:bg-muted/30 transition-colors" data-id="element-75">
                      <div className="text-sm font-medium text-foreground line-clamp-1 mb-1" data-id="element-76">
                        {r.name}
                      </div>
                      <div className="text-xs text-muted-foreground" data-id="element-77">
                        {r.dateRange} · {r.articleCount} articoli
                      </div>
                    </button>)}
                </div>
              </section>}

            {/* Report */}
            {filteredReports.length > 0 && <section data-id="element-78">
                <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-79">
                  <LayoutTemplate className="h-3.5 w-3.5" data-id="element-80" />
                  Report
                </div>
                <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border" data-id="element-81">
                  {filteredReports.map(r => <button key={r.id} onClick={() => {
              onClose();
              navigate('/reports');
            }} className="w-full text-left px-4 py-3.5 hover:bg-muted/30 transition-colors flex items-center justify-between" data-id="element-82">
                      <div className="min-w-0 flex-1" data-id="element-83">
                        <div className="text-sm font-medium text-foreground line-clamp-1 mb-1" data-id="element-84">
                          {r.name}
                        </div>
                        <div className="text-xs text-muted-foreground" data-id="element-85">
                          {r.dateRange}
                        </div>
                      </div>
                      <Badge variant="outline" className={`ml-3 flex-shrink-0 text-[10px] ${r.status === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`} data-id="element-86">
                        {r.status === 'completed' ? 'Completato' : 'Bozza'}
                      </Badge>
                    </button>)}
                </div>
              </section>}

            {/* Testate */}
            {filteredSources.length > 0 && <section data-id="element-87">
                <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-88">
                  <Building2 className="h-3.5 w-3.5" data-id="element-89" />
                  Testate
                </div>
                <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border" data-id="element-90">
                  {filteredSources.map(s => <div key={s.name} className="px-4 py-3.5 flex items-center justify-between" data-id="element-91">
                      <div className="flex items-center gap-3" data-id="element-92">
                        <img src={s.logo} alt={s.name} className="w-7 h-7 rounded-md" data-id="element-93" />
                        <span className="text-sm font-medium text-foreground" data-id="element-94">
                          {s.name}
                        </span>
                      </div>
                      <Badge className="text-[10px] px-2 py-0.5 font-normal bg-muted text-muted-foreground hover:bg-muted" data-id="element-95">
                        {tierLabels[s.tier] || s.tier}
                      </Badge>
                    </div>)}
                </div>
              </section>}
          </div>}
      </div>
    </motion.div>;
}
export function MobileTopBar() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const currentClient = clients.find(c => c.id === 'intrum') || clients[0];
  return <>
      <div className="md:hidden flex flex-col bg-card border-b border-border w-full flex-shrink-0 z-10" data-id="element-96">
        {/* Top row */}
        <div className="flex items-center justify-between px-3 h-14 gap-2" data-id="element-97">
          <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-98">
            <Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0" onClick={() => setSidebarOpen(true)} data-id="element-99">
              <Menu className="h-5 w-5" data-id="element-100" />
            </Button>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0" data-id="element-101">
            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setSearchOpen(true)} data-id="element-102">
              <Search className="h-5 w-5 text-muted-foreground" data-id="element-103" />
            </Button>
            <Avatar className="h-8 w-8 flex-shrink-0" data-id="element-104">
              <img src="https://cdn.magicpatterns.com/uploads/gMQpnDVF4sUMZFXw5c1pgV/kh4ijivu_400x400.jpg" alt="Marco C." className="w-full h-full object-cover rounded-full" data-id="element-105" />
            </Avatar>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Sheet */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen} data-id="element-106">
        <SheetContent side="left" className="w-[280px] p-0 flex flex-col" style={{
        backgroundColor: '#090E2B',
        borderRight: '1px solid rgba(255,255,255,0.08)',
        color: '#E0E0E0'
      }} data-id="element-107">
          <SheetHeader className="p-4 text-left" style={{
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }} data-id="element-108">
            <SheetTitle className="font-logo text-[20px] tracking-wide" style={{
            color: '#FFFFFF'
          }} data-id="element-109">
              Esclapon & Co.
            </SheetTitle>
          </SheetHeader>

          {/* Client Selector */}
          <div className="px-4 py-3" style={{
          backgroundColor: 'rgba(255,255,255,0.04)',
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }} data-id="element-110">
            <span className="text-[11px] uppercase tracking-wider mb-1.5 block" style={{
            color: '#8B95A5'
          }} data-id="element-111">
              Cliente selezionato:
            </span>
            <div className="flex items-center gap-2" data-id="element-112">
              <img src={currentClient.logo} alt={currentClient.name} className="max-w-[80px] max-h-[24px] object-contain object-left" style={{
              filter: 'brightness(0) invert(1)'
            }} data-id="element-113" />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-auto p-2 space-y-4" data-id="element-114">
            {navSections.map((section, idx) => <div key={idx} data-id="element-115">
                {section.title && <div className="px-3 py-2 text-[11px] uppercase tracking-wider" style={{
              color: '#6B7585'
            }} data-id="element-116">
                    {section.title}
                  </div>}
                <div className="space-y-0.5" data-id="element-117">
                  {section.items.map(item => {
                const Icon = item.icon;
                const isActive = location.pathname.startsWith(item.path);
                return <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors" style={{
                  color: isActive ? '#FFFFFF' : '#8B95A5',
                  backgroundColor: isActive ? 'rgba(77,184,232,0.12)' : 'transparent'
                }} data-id="element-118">
                        <Icon className="h-4 w-4 flex-shrink-0" style={{
                    color: isActive ? '#4DB8E8' : 'inherit'
                  }} data-id="element-119" />
                        <span className="text-sm" data-id="element-120">{item.label}</span>
                      </Link>;
              })}
                </div>
              </div>)}
          </nav>

          <div style={{
          borderTop: '1px solid rgba(255,255,255,0.08)'
        }} data-id="element-121" />

          {/* Logout */}
          <div className="p-4" data-id="element-122">
            <Link to="/" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-3 py-2 rounded-md transition-colors" style={{
            color: '#B85450'
          }} data-id="element-123">
              <LogOut className="h-4 w-4" data-id="element-124" />
              <span className="text-sm" data-id="element-125">Esci</span>
            </Link>
          </div>
        </SheetContent>
      </Sheet>

      {/* Mobile Search Overlay */}
      <AnimatePresence data-id="element-126">
        {searchOpen && <MobileSearchOverlay onClose={() => setSearchOpen(false)} data-id="element-127" />}
      </AnimatePresence>
    </>;
}